import * as THREE from 'three'
import type { Bot } from 'mineflayer'
import type { WorldRendererThree } from 'minecraft-renderer/src/three/worldRendererThree'
import type { DocumentRenderer } from 'minecraft-renderer/src/three/documentRenderer'
import { XRInventory } from './xrInventory'
import { XRServerUI } from './xrServerUI'
import { xrItemAssets } from './xrItemAssets'
import { preserveXRFov } from './xrCamera'
import { controllerRay, questControllers, gazeAngles, readStick, rotateTrackedPosition, snapTurn } from './xrInput'
import { XRTeleportClient } from './xrTeleportClient'
import { findTeleportTarget, type BlockLookup } from './xrTeleportGeometry'
import { XRTeleportVisual } from './xrTeleportVisual'
import { Vec3 } from 'vec3'
import { calibratedEye, postureEyeHeight, trackedScenePose } from './xrRig'
import { installXRChunkPump } from './xrChunks'
import { installXRInteractionRay, type XRWorldRay } from './xrInteraction'
import { XRHandRig } from './xrHandRig'
import { showXRSetup, type XRPosture } from './xrSetup'
import type { WorldView } from 'minecraft-renderer/src/worldView/worldView'
import { resumeGameAudio } from '../basicSounds'

/** Install after startWorld. This adapter uses the renderer's main-thread globals. */
export async function installThreeCubeXR(player: Bot) {
  const globals = globalThis as typeof globalThis & { world?: WorldRendererThree; documentRenderer?: DocumentRenderer; appViewer?: { worldView?: WorldView } }
  if (!globals.world || !globals.documentRenderer || !navigator.xr || !await navigator.xr.isSessionSupported('immersive-vr').catch(() => false)) return
  const world: WorldRendererThree = globals.world
  const documentRenderer: DocumentRenderer = globals.documentRenderer
  if (world.abortController.signal.aborted) return

  const renderer = world.renderer
  const rig = new THREE.Group()
  rig.name = 'threecube-xr-rig'
  world.scene.add(rig)
  const cameraParent = world.camera.parent
  const originalUpdateCamera = world.updateCamera
  const originalRender = documentRenderer.render
  const originalMouseChecks = player.mouse.beforeUpdateChecks
  const originalPreventEscape = world.reactiveState.preventEscapeMenu
  const originalVrPageRendering = world.worldRendererConfig.vrPageGameRendering
  let inventory: XRInventory | undefined
  let serverUI: XRServerUI | undefined
  let teleportVisual: XRTeleportVisual | undefined
  let locomotion: 'teleport' | 'walk' = 'teleport'
  const teleport = new XRTeleportClient(player._client)
  const helloRetry = setTimeout(() => teleport.worldReady(), 1000)
  const blockAt: BlockLookup = (x, y, z) => player.blockAt(new Vec3(x, y, z), false)
  const originalFoveation = renderer.xr.getFoveation()
  let session: XRSession | null = null
  let hands: XRHandRig | undefined
  let closeSetup: (() => void) | undefined
  let posture: XRPosture = 'seated'
  let floorSpace = false
  let baseEyeHeight = 1.62
  const baselineHead = new THREE.Vector3()
  let interactionRay: XRWorldRay | null = null
  const chunkPump = installXRChunkPump(world, () => globals.appViewer?.worldView, () => !!session)
  const restoreInteraction = installXRInteractionRay(player, () => interactionRay, () => !!session)
  // WorldRendererThree otherwise writes the desktop FOV after updateCamera and
  // before chunk culling, dropping geometry near the headset's wider edges.
  const fovWorld = world as unknown as { updateSmoothFov: () => void }
  const restoreFov = preserveXRFov(fovWorld, () => !!session)
  let frame: XRFrame | undefined
  let yawOffset = 0
  let calibrated = false
  let firstPose = true
  let turnArmed = true
  let hotbarArmed = true
  let previousButtons: Record<string, boolean> = {}
  let mining = false
  let using = false
  let closed = false
  const direction = new THREE.Vector3()
  const headPosition = new THREE.Vector3()
  const headQuaternion = new THREE.Quaternion()
  const yawQuaternion = new THREE.Quaternion()
  const up = new THREE.Vector3(0, 1, 0)
  const zero = new THREE.Vector3()

  const reticle = new THREE.Mesh(new THREE.RingGeometry(0.003, 0.005, 24), new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, depthWrite: false }))
  reticle.position.z = -1
  reticle.renderOrder = 11000

  const button = document.createElement('button')
  button.id = 'threecube-enter-vr'
  button.textContent = 'Enter VR'
  Object.assign(button.style, { position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)', zIndex: '10000', color: '#ecfff2', background: '#123d2b', border: '1px solid #82c99b', borderRadius: '10px', padding: '14px 26px', font: 'bold 18px sans-serif', cursor: 'pointer' })
  document.body.append(button)

  function releaseActions() {
    player.clearControlStates()
    if (mining) player.leftClickEnd()
    if (using) player.rightClickEnd()
    mining = false
    using = false
  }

  function visible() { return session && session.visibilityState === 'visible' }

  // The desktop focus guard otherwise stops held XR triggers when headset focus changes.
  player.mouse.beforeUpdateChecks = function () {
    if (!session) originalMouseChecks.call(this)
    else if (!visible() || inventory?.isOpen || serverUI?.isOpen || player.health <= 0) this.buttons.fill(false)
  }

  // Mineflayer publishes the absolute head yaw; feeding that yaw back into the rig
  // would rotate the headset twice. Position is assigned after tweens, below.
  world.updateCamera = function (pos, yaw, pitch, options) {
    if (!session) return originalUpdateCamera.call(this, pos, yaw, pitch, options)
    this.cameraShake.setBaseRotation(0, yawOffset)
  }

  const edge = (key: string, pressed: boolean) => {
    const rising = pressed && !previousButtons[key]
    previousButtons[key] = pressed
    return rising
  }

  function updateXR() {
    const referenceSpace = renderer.xr.getReferenceSpace()
    if (!session) return
    if (!frame || !referenceSpace || !player.entity) { cancelTrackedInput(); return }
    const pose = frame.getViewerPose(referenceSpace)
    if (!pose || !visible()) { chunkPump.frame(player.entity.position); cancelTrackedInput(); return }
    const { orientation, position } = pose.transform
    headQuaternion.set(orientation.x, orientation.y, orientation.z, orientation.w)
    direction.set(0, 0, -1).applyQuaternion(headQuaternion)
    if (!calibrated) {
      yawOffset = player.entity.yaw - gazeAngles(direction).yaw
      baselineHead.copy(position)
      baseEyeHeight = postureEyeHeight(posture, floorSpace, position.y)
      calibrated = true
    }
    const controllers = questControllers(session.inputSources)
    const left = controllers.left?.gamepad
    const right = controllers.right?.gamepad
    const leftStick = readStick(left?.axes ?? [])
    const rightStick = readStick(right?.axes ?? [])
    const pressed = (pad: Gamepad | undefined, index: number) => !!pad?.buttons[index]?.pressed
    const menuPressed = edge('menu', pressed(right, 5) || pressed(left, 5))
    const pausePressed = edge('pause', pressed(left, 4))
    const triggerEdge = edge('trigger', pressed(right, 0))
    const gripEdge = edge('grip', pressed(right, 1))
    const jumpEdge = edge('jump', pressed(right, 4))
    if (!inventory?.isOpen && !serverUI?.isOpen) {
      const turn = snapTurn(rightStick.x, turnArmed)
      yawOffset += turn.delta
      turnArmed = turn.armed
      if (Math.abs(rightStick.y) < 0.25) hotbarArmed = true
      if (hotbarArmed && Math.abs(rightStick.y) > 0.7 && Math.abs(rightStick.x) < 0.5) {
        player.setQuickBarSlot((player.quickBarSlot + (rightStick.y > 0 ? 1 : 8)) % 9)
        hotbarArmed = false
      }
    }
    // Calibrate once, then preserve bounded physical lean/crouch. World origin follows
    // the collision-constrained eyes; rig compensation only converts XR to that origin.
    yawQuaternion.setFromAxisAngle(up, yawOffset)
    headPosition.copy(rotateTrackedPosition(position, yawOffset))
    const eyeHeight = baseEyeHeight + (world.playerStateReactive.eyeHeight || 1.62) - 1.62
    const trackedEye = calibratedEye(position, baselineHead, yawOffset, player.entity.position, eyeHeight, blockAt)
    const eye = new Vec3(trackedEye.x, trackedEye.y, trackedEye.z)
    Object.assign(world.cameraWorldPos, eye)
    world.sceneOrigin.update(eye.x, eye.y, eye.z)
    world.cameraShake.setBaseRotation(0, yawOffset)
    rig.position.copy(headPosition).negate()
    rig.quaternion.copy(yawQuaternion)
    rig.updateMatrixWorld(true)
    // Three updates this again during renderer.render; doing it now supplies a
    // current camera transform to the inventory and CPU chunk culling as well.
    renderer.xr.updateCamera(world.camera)
    if (firstPose) {
      if (player.currentWindow) inventory?.open()
      firstPose = false
    }
    direction.set(0, 0, -1).applyQuaternion(headQuaternion).applyQuaternion(yawQuaternion)
    chunkPump.frame(player.entity.position)
    if (pausePressed) { releaseActions(); if (inventory?.isOpen) inventory.close(); serverUI?.toggle() }
    if (menuPressed) { releaseActions(); if (serverUI?.isOpen) serverUI.toggle(); inventory?.toggle() }
    const time = performance.now()
    // targetRaySpace is the controller's pointing ray; gripSpace is its held-object pose.
    const rightPose = controllers.right ? frame.getPose(controllers.right.targetRaySpace, referenceSpace) : null
    const leftPose = controllers.left ? frame.getPose(controllers.left.targetRaySpace, referenceSpace) : null
    const rightGripPose = controllers.right?.gripSpace ? frame.getPose(controllers.right.gripSpace, referenceSpace) : null
    const leftGripPose = controllers.left?.gripSpace ? frame.getPose(controllers.left.gripSpace, referenceSpace) : null
    hands?.update({
      right: rightPose ? { target: trackedScenePose(rightPose.transform, position, yawOffset), grip: rightGripPose ? trackedScenePose(rightGripPose.transform, position, yawOffset) : undefined } : undefined,
      left: leftPose ? { target: trackedScenePose(leftPose.transform, position, yawOffset), grip: leftGripPose ? trackedScenePose(leftGripPose.transform, position, yawOffset) : undefined } : undefined,
    })
    reticle.visible = !rightPose
    const uiRay = rightPose ? controllerRay(rightPose.transform, position, yawOffset) : { origin: zero, direction }
    const uiOrigin = new THREE.Vector3().copy(uiRay.origin)
    const uiDirection = new THREE.Vector3().copy(uiRay.direction).normalize()
    inventory?.update(uiOrigin, uiDirection, time)
    serverUI?.update(uiDirection, time, !!inventory?.isOpen, uiOrigin, world.cameraWorldPos)
    inventory?.setUiSuppressed(!!serverUI?.isOpen)
    interactionRay = rightPose && !inventory?.isOpen && !serverUI?.isOpen ? { origin: new Vec3(eye.x + uiOrigin.x, eye.y + uiOrigin.y, eye.z + uiOrigin.z), direction: new Vec3(uiDirection.x, uiDirection.y, uiDirection.z) } : null
    const angles = gazeAngles(interactionRay && (pressed(right, 0) || pressed(right, 1)) ? interactionRay.direction : direction)
    void player.look(angles.yaw, angles.pitch, true).catch(() => {})
    const allowTeleport = locomotion === 'teleport' && !!leftPose && player.health > 0 && !inventory?.isOpen && !serverUI?.isOpen && !player.currentWindow
    let arc: ReturnType<typeof findTeleportTarget> = { points: [], target: null, valid: false }
    if (allowTeleport && teleport.capabilities && leftPose && (pressed(left, 0) || teleport.aiming)) {
      const ray = controllerRay(leftPose.transform, position, yawOffset)
      arc = findTeleportTarget({ x: eye.x + ray.origin.x, y: eye.y + ray.origin.y, z: eye.z + ray.origin.z }, ray.direction, player.entity.position, blockAt, teleport.capabilities)
    }
    teleport.update(pressed(left, 0), allowTeleport, arc.valid ? arc.target : null, time)
    teleportVisual?.update(arc.points, arc.target, arc.valid, eye, teleport.aiming, teleport.blinkOpacity(time))
    if (serverUI?.isOpen) {
      releaseActions()
      if (triggerEdge) serverUI.click()
      return
    }
    if (inventory?.isOpen) {
      releaseActions()
      if (triggerEdge) void inventory.click(false, pressed(left, 1))
      if (gripEdge) void inventory.click(true, false)
      return
    }
    if (player.health <= 0) {
      releaseActions()
      if (jumpEdge) player.respawn()
      return
    }
    player.setControlState('forward', locomotion === 'walk' && leftStick.y < -0.22)
    player.setControlState('back', locomotion === 'walk' && leftStick.y > 0.22)
    player.setControlState('left', locomotion === 'walk' && leftStick.x < -0.22)
    player.setControlState('right', locomotion === 'walk' && leftStick.x > 0.22)
    player.setControlState('jump', locomotion === 'walk' && pressed(right, 4))
    player.setControlState('sprint', locomotion === 'walk' && pressed(left, 3))
    player.setControlState('sneak', pressed(left, 1))
    const wantMining = !!rightPose && pressed(right, 0)
    const wantUsing = !!rightPose && pressed(right, 1)
    if (wantMining !== mining) { mining = wantMining; if (mining) player.leftClickStart(); else player.leftClickEnd() }
    if (wantUsing !== using) { using = wantUsing; if (using) player.rightClickStart(); else player.rightClickEnd() }
    player.mouse.update()
  }

  documentRenderer.render = function (sizeChanged) {
    if (session) updateXR()
    originalRender.call(this, sizeChanged)
  }

  function cancelTrackedInput() {
    interactionRay = null
    releaseActions(); teleport.resetInput(); teleportVisual?.hide(); hands?.hide(); previousButtons = {}
    turnArmed = false; hotbarArmed = false
  }
  function visibilityChanged() { if (!visible()) cancelTrackedInput(); else void resumeGameAudio() }

  function endSession() {
    session?.removeEventListener('visibilitychange', visibilityChanged)
    session?.removeEventListener('inputsourceschange', cancelTrackedInput)
    session = null
    interactionRay = null
    frame = undefined
    releaseActions()
    teleport.resetInput()
    teleportVisual?.dispose(); teleportVisual = undefined
    hands?.dispose(); hands = undefined
    inventory?.dispose()
    inventory = undefined
    serverUI?.dispose()
    serverUI = undefined
    reticle.removeFromParent()
    world.cameraGroupVr = undefined
    cameraParent?.add(world.camera)
    world.camera.position.set(0, 0, 0)
    world.camera.quaternion.identity()
    world.reactiveState.preventEscapeMenu = originalPreventEscape
    world.worldRendererConfig.vrPageGameRendering = originalVrPageRendering
    renderer.setAnimationLoop(null)
    if (originalFoveation !== undefined) renderer.xr.setFoveation(originalFoveation)
    fovWorld.updateSmoothFov()
    button.textContent = 'Enter VR'
    if (!closed) void world.processMessageQueue('xr-exit')
    if (player.entity && !closed) world.setFirstPersonCamera(player.entity.position, player.entity.yaw, player.entity.pitch, { instant: true })
  }

  async function startSession(selectedPosture: XRPosture) {
    void resumeGameAudio()
    posture = selectedPosture
    button.disabled = true
    try {
      const nextSession = await navigator.xr!.requestSession('immersive-vr', { optionalFeatures: ['local-floor'] })
      if (closed) { await nextSession.end(); return }
      session = nextSession
      nextSession.addEventListener('end', endSession, { once: true })
      calibrated = false
      firstPose = true
      turnArmed = true
      hotbarArmed = true
      previousButtons = {}
      world.reactiveState.preventEscapeMenu = true
      world.worldRendererConfig.vrPageGameRendering = false
      renderer.xr.enabled = true
      // Request floor space when supported; local fallback is still calibrated from the first pose.
      floorSpace = !!await nextSession.requestReferenceSpace('local-floor').catch(() => null)
      if (session !== nextSession || closed) return
      renderer.xr.setReferenceSpaceType(floorSpace ? 'local-floor' : 'local')
      rig.add(world.camera)
      world.cameraGroupVr = rig
      world.camera.add(reticle)
      teleport.resetInput()
      teleportVisual = new XRTeleportVisual(world.scene, world.camera)
      hands = new XRHandRig(world.scene)
      inventory = new XRInventory(player, world.scene, world.camera, xrItemAssets, () => locomotion, () => teleport.status)
      serverUI = new XRServerUI(player, world.camera, world.scene, {
        exit: () => { void nextSession.end() },
        inventory: () => inventory?.open(),
        recenter: () => { calibrated = false; teleport.resetInput(); serverUI?.recenter() },
        locomotion: () => locomotion,
        toggleLocomotion: () => { locomotion = locomotion === 'teleport' ? 'walk' : 'teleport'; cancelTrackedInput() },
        teleportStatus: () => teleport.status,
      })
      inventory.attachHands(hands)
      serverUI.attachHands(hands)
      nextSession.addEventListener('visibilitychange', visibilityChanged)
      nextSession.addEventListener('inputsourceschange', cancelTrackedInput)
      await renderer.xr.setSession(nextSession)
      renderer.xr.setFoveation(0.7)
      const frameRateSession = nextSession as XRSession & { supportedFrameRates?: Float32Array; updateTargetFrameRate?: (rate: number) => Promise<void> }
      if (frameRateSession.supportedFrameRates?.includes(72) && frameRateSession.updateTargetFrameRate) {
        void frameRateSession.updateTargetFrameRate(72).catch(() => {})
      }
      renderer.setAnimationLoop((_time, xrFrame) => {
        if (!session) return
        frame = xrFrame
        documentRenderer.frameRender(false)
      })
      button.textContent = 'Exit VR'
    } catch (error) {
      console.error('Unable to start VR', error)
      if (session) await session.end().catch(() => {})
      endSession()
      button.textContent = 'VR failed · Try again'
      button.title = error instanceof Error ? error.message : String(error)
    } finally { button.disabled = false }
  }

  button.addEventListener('click', () => {
    if (session) { void session.end(); return }
    closeSetup?.()
    cancelTrackedInput()
    closeSetup = showXRSetup({ onStart: selected => { closeSetup = undefined; void startSession(selected) }, onCancel: () => { closeSetup = undefined } })
  })

  const dispose = () => {
    if (closed) return
    closed = true
    closeSetup?.(); closeSetup = undefined
    chunkPump.dispose()
    restoreInteraction()
    clearTimeout(helloRetry)
    teleport.dispose()
    if (session) void session.end().catch(() => {})
    endSession()
    world.updateCamera = originalUpdateCamera
    restoreFov()
    documentRenderer.render = originalRender
    player.mouse.beforeUpdateChecks = originalMouseChecks
    rig.removeFromParent()
    reticle.geometry.dispose(); reticle.material.dispose()
    button.remove()
    player.removeListener('end', dispose)
    world.abortController.signal.removeEventListener('abort', dispose)
  }
  world.abortController.signal.addEventListener('abort', dispose, { once: true })
  player.once('end', dispose)
  return dispose
}
